<template>
  <div class="sharing-table">
    <p v-if="error" class="error">{{ error.message || error.description || error }}</p>
    <toolbar
      class="new-globals"
      title="Select 1 or more users"
      :has-search="false"
      :has-menu="false"
      @onAutocompleteSelect="addPrincipal"
    >
      <template slot="search">
        <md-menu ref="principalsMenu" :md-offset-y="0.1">
          <div class="search">
            <md-icon>search</md-icon>
            <md-field
              class="md-autocomplete md-focused md-active"
              :style="{background: 'white'}"
              md-menu-trigger
            >
              <md-input v-model="state.searchTerm" placeholder="Search by name, email" />
            </md-field>
          </div>
          <md-menu-content>
            <md-menu-item
              v-for="principal in filteredUsersAndGroupsOptions"
              :key="principal.id"
              @click="addPrincipal(principal)"
            >
              <md-highlight-text :md-term="state.searchTerm">{{ principal.label }}</md-highlight-text>
              <md-button class="md-icon-button md-dense md-raised md-primary">
                <md-icon>add</md-icon>
              </md-button>
            </md-menu-item>
          </md-menu-content>
        </md-menu>
      </template>
    </toolbar>
    <md-progress-bar v-if="loading || sharing" md-mode="indeterminate" />
    <md-table
      class="users-table"
      :value="principalsWithPermissionsSorted"
      md-card
      :md-sort.sync="state.sortField"
      :md-sort-order.sync="state.sortOrder"
      @md-selected="onSelect"
    >
      <md-table-row
        slot="md-table-row"
        slot-scope="{ item: principal }"
        :md-disabled="!canChangePermission(principal)"
        md-selectable="multiple"
      >
        <md-table-cell md-label="User/Group Name" md-sort-by="name">
          <span>{{ principalName(principal) }}</span>
        </md-table-cell>
        <md-table-cell md-label="Role" md-sort-by="role">
          <span>{{ principalRole(principal) }}</span>
        </md-table-cell>
        <md-table-cell md-label="Access" md-sort-by="access">
          <md-field>
            <md-select
              class="permission-select"
              :value="getStrongestPermission(principal.permissions)"
              :disabled="!canChangePermission(principal)"
              placeholder="Select permissions"
              @input="permission => updatePermission(principal, permission)"
            >
              <md-option
                class="permission-select-option"
                :disabled="currentUserAccessLevel < accessLevelsByPermission['write']"
                value="write"
              >
                Edit
              </md-option>
              <md-option
                :disabled="currentUserAccessLevel < accessLevelsByPermission['read']"
                value="read"
              >
                View
              </md-option>
              <md-option
                :disabled="currentUserAccessLevel < accessLevelsByPermission['run']"
                value="run"
              >
                Run
              </md-option>
            </md-select>
          </md-field>
        </md-table-cell>
        <md-table-cell md-label="Sharing" md-sort-by="sharing">
          <md-checkbox
            :disabled="!principal.permissions.includes('read') || !canChangePermission(principal)"
            :value="principal.permissions.includes('share')"
            @change="value => toggleSharePermission(principal, value)"
          />
        </md-table-cell>
      </md-table-row>
    </md-table>
    <md-toolbar class="md-primary bottom-toolbar new-globals">
      <md-button
        class="md-raised md-accent"
        @click="removeSelectedPrincipalsFromSharing()"
      >
        Remove Users
      </md-button>
    </md-toolbar>
    <md-button
      class="md-raised md-primary finalize-sharing"
      :disabled="sharing"
      @click="finalizeSharing()"
    >
      Finalize Sharing
    </md-button>
  </div>
</template>

<script>
import map from 'lodash/map';
import groupBy from 'lodash/groupBy';
import mergeWith from 'lodash/mergeWith';
import isArray from 'lodash/isArray';
import filter from 'lodash/filter';
import keyBy from 'lodash/keyBy';
import maxBy from 'lodash/maxBy';
import keys from 'lodash/keys';
import cloneDeep from 'lodash/cloneDeep';
import uniq from 'lodash/uniq';
import without from 'lodash/without';
import difference from 'lodash/difference';
import get from 'lodash/get';
import intersection from 'lodash/intersection';
import sortBy from 'lodash/sortBy';
import reverse from 'lodash/reverse';
import toLower from 'lodash/toLower';

import Toolbar from '~lib/common/components/Toolbar';
import { userLabel, userRoleLabel, groupLabel } from '~lib/common/utils/labels'

const accessLevelsByPermission = {
  run: 1,
  read: 2,
  write: 3,
};

const allPermissions = ['run', 'read', 'write', 'share'];

export default {
  name: 'sharing-table',
  components: {
    Toolbar,
  },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    sharing: {
      type: Boolean,
      default: false,
    },
    users: {
      type: Array,
      default: () => [],
    },
    groups: {
      type: Array,
      default: () => [],
    },
    caseConf: {
      type: Object,
      required: true,
    },
    user: {
      type: Object,
      required: true,
    },
    error: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      state: {
        selectedPrincipalsForRemoval: [],
        updatedAccess: null,
        searchTerm: '',
        principalListOpen: false,
        sortField: 'name',
        sortOrder: 'asc',
      },
      accessLevelsByPermission,
    };
  },
  computed: {
    usersById() {
      return keyBy(this.users, 'user_id');
    },
    groupsById() {
      return keyBy(this.groups, '_id');
    },
    filteredUsersAndGroupsOptions() {
      if (!this.state.searchTerm) {
        return this.usersAndGroupsOptions;
      }
      return this.usersAndGroupsOptions.filter(
        u =>
          u.label &&
          u.label.toLowerCase().includes(this.state.searchTerm.toLowerCase()),
      );
    },
    usersAndGroupsOptions() {
      return [
        ...this.groups.map(group => ({
          type: 'group',
          id: group._id,
          label: this.principalName({ name: group.name, type: 'group' }),
        })),
        ...this.users.map(user => ({
          type: 'user',
          id: user.user_id,
          label:
            `${user.given_name || ''} ${user.family_name || ''}`.trim() ||
            user.email,
        })),
      ].filter(principal => !this.principalsWithPermissionsById[principal.id]);
    },
    principalsWithPermissionsById() {
      return keyBy(this.principalsWithPermissions, 'id');
    },
    principalsWithPermissions() {
      const { caseConf } = this;
      if (!caseConf) {
        return [];
      }
      const { updatedAccess: access } = this.state;
      if (!access) {
        return [];
      }
      const principalsWithIndividualPermissions = [
        ...map(get(access, 'write.groups'), groupId => ({
          type: 'group',
          id: groupId,
          permissions: ['write'],
        })),
        ...map(get(access, 'write.users'), userId => ({
          type: 'user',
          id: userId,
          permissions: ['write'],
        })),
        ...map(get(access, 'read.groups'), groupId => ({
          type: 'group',
          id: groupId,
          permissions: ['read'],
        })),
        ...map(get(access, 'read.users'), userId => ({
          type: 'user',
          id: userId,
          permissions: ['read'],
        })),
        ...map(get(access, 'run.groups'), groupId => ({
          type: 'group',
          id: groupId,
          permissions: ['run'],
        })),
        ...map(get(access, 'run.users'), userId => ({
          type: 'user',
          id: userId,
          permissions: ['run'],
        })),
        ...map(get(access, 'share.groups'), groupId => ({
          type: 'group',
          id: groupId,
          permissions: ['share'],
        })),
        ...map(get(access, 'share.users'), userId => ({
          type: 'user',
          id: userId,
          permissions: ['share'],
        })),
      ];

      const mergeCustomizer = (objValue, srcValue) => {
        if (isArray(objValue)) {
          return objValue.concat(srcValue);
        }
        return undefined;
      };

      const principalsWithMergedPermissions = map(
        groupBy(
          principalsWithIndividualPermissions,
          principal => `${principal.type}-${principal.id}`,
        ),
        group => mergeWith(...group, mergeCustomizer),
      );
      const principalsWithData = map(
        principalsWithMergedPermissions,
        principal => {
          if (principal.type === 'group') {
            const group = this.groupsById[principal.id];
            return group ? { ...group, ...principal } : null;
          } else if (principal.type === 'user') {
            const user = this.usersById[principal.id];
            return user ? { ...user, ...principal } : null;
          }
          return null;
        },
      );
      const nonNullPrincipals = filter(principalsWithData, p => !!p);
      return nonNullPrincipals;
    },
    principalsWithPermissionsSorted() {
      const { sortField, sortOrder } = this.state;
      const sorted = sortBy(this.principalsWithPermissions, principal => {
        if (sortField === 'name') {
          return toLower(this.principalName(principal));
        }
        if (sortField === 'role') {
          return this.principalRole(principal);
        }
        if (sortField === 'access') {
          return accessLevelsByPermission[
            this.getStrongestPermission(principal)
          ];
        }
        if (sortField === 'sharing') {
          return principal.permissions.includes('share') ? 0 : 1;
        }
        return 0;
      });
      if (sortOrder === 'desc') {
        return reverse(sorted);
      }
      return sorted;
    },
    currentUserAccessLevel() {
      const caseAccess = this.caseConf.access;
      const {user} = this;
      if (caseAccess.owner === user.id) {
        return 11;
      }
      const permissions = ['write', 'read', 'run'];
      // eslint-disable-next-line no-restricted-syntax
      for (const permission of permissions) {
        if (this.hasPermission(permission)) {
          return accessLevelsByPermission[permission];
        }
      }
      return 0;
    },
  },
  watch: {
    sharing(newVal, oldVal) {
      if (oldVal && !newVal && !this.error) {
        this.$emit('close');
        this.$emit('update', {
          id: this.caseConf.id,
          access: this.state.updatedAccess,
        });
      }
    },
    'state.searchTerm'(searchTerm) {
      if (!searchTerm) {
        return;
      }
      if (!this.$refs.principalsMenu) {
        return;
      }
      if (!this.$refs.principalsMenu.isActive) {
        this.$refs.principalsMenu.toggleContent();
      }
    },
  },
  created() {
    this.state.updatedAccess = this.caseConf
      ? cloneDeep(this.caseConf.access)
      : null;
  },
  methods: {
    onSelect(principals) {
      this.state.selectedPrincipalsForRemoval = principals;
    },
    principalName(principal) {
      if (principal.type === 'user') {
        return userLabel(principal)
      } else if (principal.type === 'group') {
        return groupLabel(principal);
      }
      return principal.name;
    },
    principalRole(principal) {
      const orgId = this.user.groups[0];
      if (principal.type === 'user') {
        return userRoleLabel(principal, orgId);
      }
      return '--';
    },
    getStrongestPermission(permissions) {
      return maxBy(permissions, p => accessLevelsByPermission[p]);
    },
    getImpliedPermissions(permission) {
      return filter(
        keys(accessLevelsByPermission),
        p => accessLevelsByPermission[p] <= accessLevelsByPermission[permission],
      );
    },
    updatePermission(principal, permission) {
      const impliedPermissions = this.getImpliedPermissions(permission);
      const omittedPermissions = difference(
        keys(accessLevelsByPermission),
        impliedPermissions,
      );
      this.addPermissionsToPrincipal(principal, impliedPermissions);
      this.removePermissionsFromPrincipal(principal, omittedPermissions);
      if (permission === 'run') {
        this.toggleSharePermission(principal, false);
      }
    },
    addPrincipal(principal) {
      this.addPermissionsToPrincipal(principal, ['run']);
    },
    toggleSharePermission(principal, shared) {
      if (shared) {
        this.addPermissionsToPrincipal(principal, ['share']);
      } else {
        this.removePermissionsFromPrincipal(principal, ['share']);
      }
    },
    addPermissionsToPrincipal(principal, permissions) {
      const { updatedAccess } = this.state;
      permissions.forEach(perm => {
        updatedAccess[perm] = updatedAccess[perm] || {};
        updatedAccess[perm][`${principal.type}s`] =
          updatedAccess[perm][`${principal.type}s`] || [];
        updatedAccess[perm][`${principal.type}s`] = uniq(
          updatedAccess[perm][`${principal.type}s`].concat(principal.id),
        );
      });
    },
    removePermissionsFromPrincipal(principal, permissions) {
      const { updatedAccess } = this.state;
      permissions.forEach(perm => {
        updatedAccess[perm] = updatedAccess[perm] || {};
        updatedAccess[perm][`${principal.type}s`] =
          updatedAccess[perm][`${principal.type}s`] || [];
        updatedAccess[perm][`${principal.type}s`] = without(
          updatedAccess[perm][`${principal.type}s`],
          principal.id,
        );
      });
    },
    removeSelectedPrincipalsFromSharing() {
      const { selectedPrincipalsForRemoval } = this.state;
      if (
        !selectedPrincipalsForRemoval ||
        !selectedPrincipalsForRemoval.length
      ) {
        return;
      }
      selectedPrincipalsForRemoval.forEach(principal => {
        this.removePermissionsFromPrincipal(principal, allPermissions);
      });
    },
    canChangePermission(principal) {
      if (principal.id === this.user.id) {
        return false;
      }
      return (
        accessLevelsByPermission[
          this.getStrongestPermission(principal.permissions)
        ] <= this.currentUserAccessLevel
      );
    },
    hasPermission(permission) {
      const caseAccess = this.caseConf.access;
      const {user} = this;
      const userId = user.id;
      const userGroups = user.groups;
      const accessSpecification = get(caseAccess, permission, {});
      if (!accessSpecification) {
        return false;
      }
      return (
        (accessSpecification.users &&
          accessSpecification.users.includes(userId)) ||
        intersection(accessSpecification.groups, userGroups).length !== 0
      );
    },
    finalizeSharing() {
      this.$emit('updateAccess', this.caseConf, this.state.updatedAccess);
    },
  },
};
</script>

<style scoped>
.sharing-table {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.error {
  color: red;
}

.md-table {
  flex: 1;
}

.md-button.finalize-sharing {
  margin-right: 0;
  align-self: flex-end;
}

.bottom-toolbar {
  display: flex;
  justify-content: flex-end;
}

.autocomplete-result {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>

<style>
.permission-select-option[disabled] button {
  text-decoration: line-through !important;
}
</style>